import { EmploymentEntrySchema, type FinancialAidSections } from '@triedge/financial-aid-domain';
import type { FinancialAidRequirementPolicy } from '@triedge/staff-settings-domain';
import {
  FINANCIAL_AID_REQUIREMENT_FIELDS,
  isYstnjFinancialAidRequirementFieldVisible,
} from './financialAidRequirementFields';
import {
  resolveParentGuardianHouseholdComposition,
  type ParentGuardianFormKey,
} from '../shared/parentGuardianHousehold';

const PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY = 'parentCollectedFinancialAidSnapshot';
const REDACTED_EMPLOYER_NAME = 'Employer hidden';
export const YSTNJ_HIDDEN_EMPLOYER_RETAINED_EMPLOYMENT_FIELDS = [
  'position',
  'startDate',
  'annualSalary',
  'parsonageAmount',
  'federalTax',
  'stateTax',
  'ficaTax',
  'isPrimaryJob',
  'hoursPerWeek',
] as const;
const OCCUPATION_IDENTIFYING_KEYS = [
  'position',
  'occupation',
  'jobTitle',
  'title',
] as const;
type CanonicalScrubber = (
  sections: Record<string, unknown>,
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
) => void;
type ParentSectionKey = 'parent1' | 'parent2';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const cloneSerializable = <T>(value: T): T => {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value)) as T;
};

const readNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const clearPath = (
  target: Record<string, unknown>,
  path: string,
  mode: 'blank' | 'delete',
): void => {
  const segments = path.split('.').filter(Boolean);
  if (segments.length === 0) return;

  let cursor: Record<string, unknown> = target;
  for (const segment of segments.slice(0, -1)) {
    const next = cursor[segment];
    // Array-row paths (e.g. camps2025.0.fee) walk into the row record.
    if (!isRecord(next) && !Array.isArray(next)) return;
    cursor = next as unknown as Record<string, unknown>;
  }

  const leaf = segments[segments.length - 1];
  if (mode === 'delete') {
    delete cursor[leaf];
    return;
  }
  cursor[leaf] = '';
};

const ARRAY_COLUMN_REQUIREMENT_PATHS = new Map(
  FINANCIAL_AID_REQUIREMENT_FIELDS.flatMap((field) => {
    const condition = field.condition;
    const segments = field.path.split('.');
    if (
      condition?.kind !== 'arrayRow'
      || !condition.arrayKey
      || segments.length !== 3
      || segments[0] !== condition.arrayKey
      || segments[1] !== '0'
      || !segments[2]
    ) {
      return [];
    }
    return [[field.path, { arrayKey: condition.arrayKey, fieldKey: segments[2] }]];
  }),
);

const clearHiddenRequirementPath = (
  target: Record<string, unknown>,
  path: string,
  mode: 'blank' | 'delete',
): void => {
  const arrayColumn = ARRAY_COLUMN_REQUIREMENT_PATHS.get(path);
  if (!arrayColumn) {
    clearPath(target, path, mode);
    return;
  }

  const rows = target[arrayColumn.arrayKey];
  if (!Array.isArray(rows)) return;
  rows.forEach((row) => {
    if (!isRecord(row)) return;
    if (mode === 'delete') {
      delete row[arrayColumn.fieldKey];
      return;
    }
    row[arrayColumn.fieldKey] = '';
  });
};

const readRawSnapshot = (sections: Record<string, unknown>): Record<string, unknown> | null => {
  const additionalInfo = isRecord(sections.additionalInfo) ? sections.additionalInfo : null;
  if (!additionalInfo) return null;
  const rawSnapshot = additionalInfo[PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY];
  return isRecord(rawSnapshot) ? rawSnapshot : null;
};

const scrubSnapshotParentPii = (rawSnapshot: Record<string, unknown>): void => {
  for (const parentKey of ['father', 'mother'] as const) {
    const parent = rawSnapshot[parentKey];
    if (!isRecord(parent)) continue;
    for (const key of Object.keys(parent)) {
      if (key === 'businessName' || key === 'shul' || key === 'rabbi') continue;
      delete parent[key];
    }
  }
};

const isHiddenRequirementField = (
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
  fieldKey: string,
): boolean => {
  const field = FINANCIAL_AID_REQUIREMENT_FIELDS.find((candidate) => candidate.fieldKey === fieldKey);
  return Boolean(field && !isYstnjFinancialAidRequirementFieldVisible(requirementPolicy, field));
};

const readParentKeyFromRelationship = (value: unknown): ParentGuardianFormKey | null => {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (['father', 'dad', 'stepfather', 'stepdad', 'parent1', 'guardian1'].includes(normalized)) return 'father';
  if (['mother', 'mom', 'stepmother', 'stepmom', 'parent2', 'guardian2'].includes(normalized)) return 'mother';
  return null;
};

const resolveCanonicalParentSectionKey = (
  sections: Record<string, unknown>,
  parentKey: ParentGuardianFormKey,
): ParentSectionKey | null => {
  const parent1 = isRecord(sections.parent1) ? sections.parent1 : null;
  const parent2 = isRecord(sections.parent2) ? sections.parent2 : null;
  if (parent1 && readParentKeyFromRelationship(parent1.relationship) === parentKey) return 'parent1';
  if (parent2 && readParentKeyFromRelationship(parent2.relationship) === parentKey) return 'parent2';

  const householdComposition = resolveParentGuardianHouseholdComposition(sections);
  if (householdComposition.mode === 'single_parent_guardian') {
    return householdComposition.primaryKey === parentKey ? 'parent1' : null;
  }
  return parentKey === 'father' ? 'parent1' : 'parent2';
};

const resolveCanonicalParentIndex = (
  sections: Record<string, unknown>,
  parentKey: ParentGuardianFormKey,
): number | null => {
  const sectionKey = resolveCanonicalParentSectionKey(sections, parentKey);
  if (sectionKey === 'parent1') return 1;
  if (sectionKey === 'parent2') return 2;
  return null;
};

const buildRedactedEmployerEmploymentEntry = (
  entry: Record<string, unknown>,
  parentIndex: number,
): Record<string, unknown> | null => {
  const next: Record<string, unknown> = {
    parentIndex,
    employerName: REDACTED_EMPLOYER_NAME,
  };
  for (const key of YSTNJ_HIDDEN_EMPLOYER_RETAINED_EMPLOYMENT_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(entry, key)) next[key] = entry[key];
  }
  const parsed = EmploymentEntrySchema.safeParse(next);
  if (parsed.success) return parsed.data;
  console.warn('[financial-aid-hidden-fields] Dropped hidden-employer employment row after redaction.', {
    parentIndex,
    reason: 'employment_schema_validation_failed',
  });
  return null;
};

const clearCanonicalParentField = (
  sections: Record<string, unknown>,
  parentKey: ParentGuardianFormKey,
  fieldName: 'occupation' | 'employer',
): void => {
  const sectionKey = resolveCanonicalParentSectionKey(sections, parentKey);
  const parent = sectionKey ? sections[sectionKey] : null;
  if (isRecord(parent)) {
    delete parent[fieldName];
  }

  if (!isRecord(sections.employment) || !Array.isArray(sections.employment.employments)) return;
  if (!sectionKey) return;
  const employment = sections.employment;
  const employments = employment.employments as unknown[];

  const parentIndex = resolveCanonicalParentIndex(sections, parentKey);
  if (parentIndex === null) return;

  employment.employments = employments
    .map((entry: unknown) => {
      if (!isRecord(entry) || entry.parentIndex !== parentIndex) return entry;
      if (fieldName === 'employer') {
        return buildRedactedEmployerEmploymentEntry(entry, parentIndex);
      }
      const next = { ...entry };
      OCCUPATION_IDENTIFYING_KEYS.forEach((key) => {
        delete next[key];
      });
      return next;
    })
    .filter(Boolean);
};

const recalculateLiabilities = (sections: Record<string, unknown>): void => {
  const liabilities = isRecord(sections.liabilities) ? sections.liabilities : null;
  const entries = Array.isArray(liabilities?.entries) ? liabilities.entries.filter(isRecord) : null;
  if (!liabilities || !entries) return;
  liabilities.totalLiabilities = entries.reduce((sum, entry) => sum + readNumber(entry.currentBalance), 0);
  liabilities.totalMonthlyDebtPayments = entries.reduce((sum, entry) => sum + readNumber(entry.monthlyPayment), 0);
};

const removeLiabilityEntries = (
  sections: Record<string, unknown>,
  predicate: (entry: Record<string, unknown>) => boolean,
): void => {
  const liabilities = isRecord(sections.liabilities) ? sections.liabilities : null;
  if (!Array.isArray(liabilities?.entries)) return;
  liabilities.entries = liabilities.entries.filter((entry) => !isRecord(entry) || !predicate(entry));
  recalculateLiabilities(sections);
};

const removeExpenseEntriesByCategories = (
  sections: Record<string, unknown>,
  categories: readonly string[],
): void => {
  const expenses = isRecord(sections.expenses) ? sections.expenses : null;
  if (!Array.isArray(expenses?.entries)) return;
  const categorySet = new Set(categories);
  expenses.entries = expenses.entries.filter((entry) => !isRecord(entry) || !categorySet.has(String(entry.category ?? '')));
};

const sumExpenseCategory = (sections: Record<string, unknown>, category: string): number => {
  const expenses = isRecord(sections.expenses) ? sections.expenses : null;
  const entries = Array.isArray(expenses?.entries) ? expenses.entries.filter(isRecord) : [];
  return entries
    .filter((entry) => entry.category === category)
    .reduce((sum, entry) => sum + readNumber(entry.monthlyAmount), 0);
};

const sumDebtExpenseMonthlyPayments = (sections: Record<string, unknown>): number => {
  const liabilities = isRecord(sections.liabilities) ? sections.liabilities : null;
  const entries = Array.isArray(liabilities?.entries) ? liabilities.entries.filter(isRecord) : [];
  return entries.reduce((sum, entry) => {
    const type = String(entry.type ?? '');
    if (type !== 'student_loan' && type !== 'personal_loan') return sum;
    return sum + readNumber(entry.monthlyPayment);
  }, 0);
};

const recomputeTotalMonthlyExpenses = (sections: Record<string, unknown>): void => {
  const expenses = isRecord(sections.expenses) ? sections.expenses : null;
  if (!expenses) return;
  expenses.totalMonthlyExpenses =
    readNumber(expenses.housingCost)
    + readNumber(expenses.utilitiesTotal)
    + readNumber(expenses.foodTotal)
    + readNumber(expenses.healthcareTotal)
    + readNumber(expenses.insuranceTotal)
    + readNumber(expenses.otherTotal)
    + sumDebtExpenseMonthlyPayments(sections);
};

const removeExpenseEntriesByMatcher = (
  sections: Record<string, unknown>,
  matcher: (entry: Record<string, unknown>) => boolean,
): boolean => {
  const expenses = isRecord(sections.expenses) ? sections.expenses : null;
  if (!Array.isArray(expenses?.entries)) return false;
  const entries = expenses.entries as unknown[];
  const before = entries.length;
  const nextEntries = entries.filter((entry) => !isRecord(entry) || !matcher(entry));
  expenses.entries = nextEntries;
  return nextEntries.length !== before;
};

const readSourceRequirementFieldKey = (entry: Record<string, unknown>): string =>
  String(entry.sourceRequirementFieldKey ?? '').trim();

const scrubSharedExpenseCategory = (
  sections: Record<string, unknown>,
  config: {
    fieldKeys: readonly string[];
    category: string;
    totalKey: 'healthcareTotal' | 'insuranceTotal' | 'otherTotal';
  },
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
): void => {
  const hiddenFields = config.fieldKeys.filter((fieldKey) => isHiddenRequirementField(requirementPolicy, fieldKey));
  if (hiddenFields.length === 0) return;

  const expenses = isRecord(sections.expenses) ? sections.expenses : null;
  if (!expenses) return;

  if (hiddenFields.length === config.fieldKeys.length) {
    removeExpenseEntriesByCategories(sections, [config.category]);
    expenses[config.totalKey] = 0;
    recomputeTotalMonthlyExpenses(sections);
    return;
  }

  const removed = removeExpenseEntriesByMatcher(sections, (entry) =>
    entry.category === config.category
    && (hiddenFields.includes(readSourceRequirementFieldKey(entry)) || readSourceRequirementFieldKey(entry) === '')
  );
  expenses[config.totalKey] = sumExpenseCategory(sections, config.category);
  if (removed) recomputeTotalMonthlyExpenses(sections);
};

const scrubHealthInsurance = (
  sections: Record<string, unknown>,
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
): void => {
  const expenses = isRecord(sections.expenses) ? sections.expenses : null;
  if (!expenses) return;
  if (isHiddenRequirementField(requirementPolicy, 'expenses.healthInsurance')) {
    delete expenses.healthInsurance;
  }
  scrubSharedExpenseCategory(
    sections,
    {
      fieldKeys: ['expenses.healthInsurance'],
      category: 'healthcare',
      totalKey: 'healthcareTotal',
    },
    requirementPolicy,
  );
};

const scrubMedicalCoverage = (sections: Record<string, unknown>): void => {
  const expenses = isRecord(sections.expenses) ? sections.expenses : null;
  if (!expenses) return;
  delete expenses.medicalCoverage;
  delete expenses.medicalCoverageDetails;
  const specialCircumstances = isRecord(sections.specialCircumstances)
    ? sections.specialCircumstances
    : null;
  if (specialCircumstances) {
    specialCircumstances.hasMedicalExpenses = false;
    specialCircumstances.medicalExpensesDetails = null;
  }
};

const scrubInsuranceCategory: CanonicalScrubber = (sections, requirementPolicy) => {
  scrubSharedExpenseCategory(
    sections,
    {
      fieldKeys: ['expenses.carInsurance', 'expenses.lifeInsurance'],
      category: 'insurance',
      totalKey: 'insuranceTotal',
    },
    requirementPolicy,
  );
};

const scrubOtherCategory: CanonicalScrubber = (sections, requirementPolicy) => {
  scrubSharedExpenseCategory(
    sections,
    {
      fieldKeys: ['expenses.shulDues', 'expenses.cleaningHelp', 'expenses.other'],
      category: 'other',
      totalKey: 'otherTotal',
    },
    requirementPolicy,
  );
};

const scrubRawOnlyFieldPath = (fieldPath: string): CanonicalScrubber => (sections) => {
  clearHiddenRequirementPath(sections, fieldPath, 'delete');
};

const readTextValue = (value: unknown): string => String(value ?? '').trim();

const PARENT_INCOME_ENTRY_DESCRIPTIONS: Record<ParentGuardianFormKey, string> = {
  father: 'Father income',
  mother: 'Mother income',
};

const scrubParentGrossIncome = (
  sections: Record<string, unknown>,
  parentKey: ParentGuardianFormKey,
): void => {
  const householdComposition = resolveParentGuardianHouseholdComposition(sections);
  const income = isRecord(sections.income) ? sections.income : null;
  if (income && Array.isArray(income.entries)) {
    const isSingleParentHousehold = householdComposition.mode === 'single_parent_guardian';
    if (!isSingleParentHousehold || householdComposition.primaryKey === parentKey) {
      income.entries = income.entries.filter((entry) => {
        if (!isRecord(entry) || entry.type !== 'salary_wages') return true;
        // Single-parent households label the sole salary entry with a custom
        // role label, so the only attributable salary entry is removed whole.
        if (isSingleParentHousehold) return false;
        return entry.description !== PARENT_INCOME_ENTRY_DESCRIPTIONS[parentKey];
      });
    }
    const remainingSalaryTotal = (income.entries as unknown[])
      .filter(isRecord)
      .filter((entry) => entry.type === 'salary_wages')
      .reduce((sum, entry) => sum + readNumber(entry.annualAmount), 0);
    income.totalAnnualIncome = remainingSalaryTotal;
    income.previousYearIncome = remainingSalaryTotal;
  }

  const employment = isRecord(sections.employment) ? sections.employment : null;
  if (employment && Array.isArray(employment.employments)) {
    const parentIndex = resolveCanonicalParentIndex(sections, parentKey);
    if (parentIndex !== null) {
      employment.employments = employment.employments.map((entry) => {
        if (!isRecord(entry) || entry.parentIndex !== parentIndex) return entry;
        return { ...entry, annualSalary: 0 };
      });
    }
  }
};

type MajorExpenditureEntryScrubTarget = {
  description: string;
  amountFieldKey: string;
  reasonFieldKey: string;
};

const MAJOR_EXPENDITURE_ENTRY_TARGETS: readonly MajorExpenditureEntryScrubTarget[] = [
  {
    description: 'Home improvements',
    amountFieldKey: 'majorExp.homeImprovements',
    reasonFieldKey: 'majorExp.homeImprovementsDetails',
  },
  {
    description: 'Large purchases',
    amountFieldKey: 'majorExp.largePurchases',
    reasonFieldKey: 'majorExp.largePurchasesDetails',
  },
  {
    description: 'Simcha',
    amountFieldKey: 'majorExp.simchaCost',
    reasonFieldKey: 'majorExp.simchaType',
  },
];

const scrubMajorExpenditureEntries: CanonicalScrubber = (sections, requirementPolicy) => {
  const majorExpenditures = isRecord(sections.majorExpenditures) ? sections.majorExpenditures : null;
  if (!majorExpenditures || !Array.isArray(majorExpenditures.entries)) return;
  const nextEntries = majorExpenditures.entries
    .filter(isRecord)
    .map((entry): Record<string, unknown> | null => {
      const target = MAJOR_EXPENDITURE_ENTRY_TARGETS.find((candidate) =>
        candidate.description === entry.description);
      if (!target) return entry;
      const amountHidden = isHiddenRequirementField(requirementPolicy, target.amountFieldKey);
      const reasonHidden = isHiddenRequirementField(requirementPolicy, target.reasonFieldKey);
      if (!amountHidden && !reasonHidden) return entry;
      const next = { ...entry };
      if (amountHidden) next.expectedAmount = 0;
      if (reasonHidden) next.reason = null;
      if (amountHidden && !readTextValue(next.reason)) return null;
      return next;
    })
    .filter((entry): entry is Record<string, unknown> => Boolean(entry));
  majorExpenditures.entries = nextEntries;
  majorExpenditures.hasPlannedExpenditures = nextEntries.length > 0;
  majorExpenditures.totalPlannedExpenditures = nextEntries.reduce(
    (sum, entry) => sum + readNumber(entry.expectedAmount),
    0,
  );
  if (isHiddenRequirementField(requirementPolicy, 'majorExp.simchaType')) {
    majorExpenditures.simchaDetails = null;
    if (isHiddenRequirementField(requirementPolicy, 'majorExp.simchaCost')) {
      majorExpenditures.hasSimchaPlanned = false;
    }
  }
};

const scrubExtraordinaryObligations: CanonicalScrubber = (sections) => {
  const specialCircumstances = isRecord(sections.specialCircumstances)
    ? sections.specialCircumstances
    : null;
  if (!specialCircumstances) return;
  specialCircumstances.hasUnusualExpenses = false;
  specialCircumstances.unusualExpensesDescription = null;
};

const scrubMortgageYears: CanonicalScrubber = (sections) => {
  clearPath(sections, 'assets.mortgageYears', 'delete');
  clearPath(sections, 'realEstate.mortgageYears', 'delete');
  const realEstate = isRecord(sections.realEstate) ? sections.realEstate : null;
  const properties = Array.isArray(realEstate?.properties) ? realEstate.properties.filter(isRecord) : [];
  properties.forEach((property) => {
    delete property.mortgageYears;
    delete property.mortgageTermYears;
  });
};

export const YSTNJ_FINANCIAL_AID_CANONICAL_REQUIREMENT_FIELD_SCRUBBERS: Partial<Record<string, CanonicalScrubber>> = {
  'father.occupation': (sections) => clearCanonicalParentField(sections, 'father', 'occupation'),
  'father.employer': (sections) => clearCanonicalParentField(sections, 'father', 'employer'),
  'mother.occupation': (sections) => clearCanonicalParentField(sections, 'mother', 'occupation'),
  'mother.employer': (sections) => clearCanonicalParentField(sections, 'mother', 'employer'),
  'incomeFather.gross2024': (sections) => scrubParentGrossIncome(sections, 'father'),
  'incomeMother.gross2024': (sections) => scrubParentGrossIncome(sections, 'mother'),
  'incomeFather.parsonage': scrubRawOnlyFieldPath('incomeFather.parsonage'),
  'incomeMother.parsonage': scrubRawOnlyFieldPath('incomeMother.parsonage'),
  'incomeFather.refunds': scrubRawOnlyFieldPath('incomeFather.refunds'),
  'incomeMother.refunds': scrubRawOnlyFieldPath('incomeMother.refunds'),
  'incomeFather.estimated2025': scrubRawOnlyFieldPath('incomeFather.estimated2025'),
  'incomeMother.estimated2025': scrubRawOnlyFieldPath('incomeMother.estimated2025'),
  'benefits.healthInsurance': scrubRawOnlyFieldPath('benefits.healthInsurance'),
  'benefits.tuition': scrubRawOnlyFieldPath('benefits.tuition'),
  'benefits.auto': scrubRawOnlyFieldPath('benefits.auto'),
  'benefits.other': scrubRawOnlyFieldPath('benefits.other'),
  'majorExp.homeImprovements': scrubMajorExpenditureEntries,
  'majorExp.homeImprovementsDetails': scrubMajorExpenditureEntries,
  'majorExp.largePurchases': scrubMajorExpenditureEntries,
  'majorExp.largePurchasesDetails': scrubMajorExpenditureEntries,
  'majorExp.simchaType': scrubMajorExpenditureEntries,
  'majorExp.simchaCost': scrubMajorExpenditureEntries,
  'majorExp.loanAmount': scrubRawOnlyFieldPath('majorExp.loanAmount'),
  'majorExp.extraordinaryObligations': scrubExtraordinaryObligations,
  'vacation.cost': scrubRawOnlyFieldPath('vacation.cost'),
  'camps2025.0.childName': scrubRawOnlyFieldPath('camps2025.0.childName'),
  'camps2025.0.campName': scrubRawOnlyFieldPath('camps2025.0.campName'),
  'camps2025.0.fee': scrubRawOnlyFieldPath('camps2025.0.fee'),
  'camps2024.0.childName': scrubRawOnlyFieldPath('camps2024.0.childName'),
  'camps2024.0.campName': scrubRawOnlyFieldPath('camps2024.0.campName'),
  'camps2024.0.fee': scrubRawOnlyFieldPath('camps2024.0.fee'),
  'request.offeredTuition': scrubRawOnlyFieldPath('request.offeredTuition'),
  'request.offeredFees': scrubRawOnlyFieldPath('request.offeredFees'),
  'incomeFather.fedTax': scrubRawOnlyFieldPath('incomeFather.fedTax'),
  'incomeFather.stateTax': scrubRawOnlyFieldPath('incomeFather.stateTax'),
  'incomeFather.ficaTax': scrubRawOnlyFieldPath('incomeFather.ficaTax'),
  'incomeMother.fedTax': scrubRawOnlyFieldPath('incomeMother.fedTax'),
  'incomeMother.stateTax': scrubRawOnlyFieldPath('incomeMother.stateTax'),
  'incomeMother.ficaTax': scrubRawOnlyFieldPath('incomeMother.ficaTax'),
  'assets.mortgageYears': scrubMortgageYears,
  'expenses.studentLoans': (sections) => {
    removeLiabilityEntries(sections, (entry) => entry.type === 'student_loan');
    recomputeTotalMonthlyExpenses(sections);
  },
  'expenses.carInsurance': scrubInsuranceCategory,
  'expenses.healthInsurance': scrubHealthInsurance,
  'expenses.lifeInsurance': scrubInsuranceCategory,
  'expenses.shulDues': scrubOtherCategory,
  'expenses.cleaningHelp': scrubOtherCategory,
  'expenses.other': scrubOtherCategory,
  'expenses.medicalCoverage': (sections) => scrubMedicalCoverage(sections),
};

export const listMissingYstnjFinancialAidCanonicalScrubbers = (): string[] =>
  FINANCIAL_AID_REQUIREMENT_FIELDS
    .map((field) => field.fieldKey)
    .filter((fieldKey) => !YSTNJ_FINANCIAL_AID_CANONICAL_REQUIREMENT_FIELD_SCRUBBERS[fieldKey]);

const scrubCanonicalSectionsForHiddenFields = (
  sections: Record<string, unknown>,
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
): void => {
  for (const field of FINANCIAL_AID_REQUIREMENT_FIELDS) {
    if (isYstnjFinancialAidRequirementFieldVisible(requirementPolicy, field)) continue;
    const scrubber = YSTNJ_FINANCIAL_AID_CANONICAL_REQUIREMENT_FIELD_SCRUBBERS[field.fieldKey];
    if (scrubber) {
      scrubber(sections, requirementPolicy);
      continue;
    }
    throw new Error(`[financial-aid-hidden-fields] Missing canonical scrubber for ${field.fieldKey}.`);
  }
};

export const clearYstnjFinancialAidHiddenRequirementValues = <T>(
  data: T,
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
): T => {
  const cloned = cloneSerializable(data);
  if (!isRecord(cloned)) return cloned;

  for (const field of FINANCIAL_AID_REQUIREMENT_FIELDS) {
    if (isYstnjFinancialAidRequirementFieldVisible(requirementPolicy, field)) continue;
    clearHiddenRequirementPath(cloned, field.path, 'blank');
  }

  return cloned;
};

export const scrubYstnjFinancialAidHiddenRequirementSectionsPatch = <T>(
  sections: T,
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
): T => {
  const cloned = cloneSerializable(sections);
  if (!isRecord(cloned)) return cloned;

  const rawSnapshot = readRawSnapshot(cloned);
  if (rawSnapshot) {
    scrubSnapshotParentPii(rawSnapshot);
    for (const field of FINANCIAL_AID_REQUIREMENT_FIELDS) {
      if (isYstnjFinancialAidRequirementFieldVisible(requirementPolicy, field)) continue;
      clearHiddenRequirementPath(rawSnapshot, field.path, 'delete');
    }
  }

  scrubCanonicalSectionsForHiddenFields(cloned, requirementPolicy);
  return cloned;
};

export const scrubYstnjFinancialAidHiddenRequirementSections = (
  sections: FinancialAidSections,
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
): FinancialAidSections =>
  scrubYstnjFinancialAidHiddenRequirementSectionsPatch(sections, requirementPolicy);
